/* global fbq */

import React, { useState, useEffect } from 'react';
import LPTooltip from '../../components/LPTooltip';
import PremiumSelect from '../../components/PremiumSelect';
import { faLocationCrosshairs, faUsers, faUserGroup, faUserClock, faUser, faWifi, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

import { Helmet } from 'react-helmet-async';
import OG from '../../assets/og/normal.png';

const roles = [
  { key: 'Top', icon: require('../../assets/Positions/Top.png') },
  { key: 'Jungle', icon: require('../../assets/Positions/Jungle.png') },
  { key: 'Mid', icon: require('../../assets/Positions/Mid.png') },
  { key: 'ADC', icon: require('../../assets/Positions/ADC.png') },
  { key: 'Support', icon: require('../../assets/Positions/Support.png') }
];

const rawChamps = require('../../constants/champions.json');
const champions = Array.isArray(rawChamps) ? rawChamps : Object.values(rawChamps);

export default function NormalBoosting({ updatePrice, order_details, checkoutInit, loading }) {
    
    const currency = localStorage.getItem('currency') || 'USD';
    const { getAccessToken } = useAuth();
    const [addonsOpen, setAddonsOpen] = useState(false);

    const [region, setRegion] = useState('NA');
    const [orderType, setOrderType] = useState('Total Games'); // or 'Total Wins'
    const [numGames, setNumGames] = useState('10');
    const [addons, setAddons] = useState([]);
    const [searchChamp, setSearchChamp] = useState('');
    const [selectedChamps, setSelectedChamps] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
    const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setInitialLoad(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('normal-boosting-data');
    if (stored) {
        const data = JSON.parse(stored);
        setRegion(data.region || 'NA');
        setOrderType(data.orderType || 'Total Games');
        setNumGames(data.numGames || '10');
        setAddons(data.addons || []);
        setSelectedChamps(data.selectedChamps || []);
        setSelectedRoles(data.selectedRoles || []);
        calculatePrice();
        order_details([
            ['Boost Type', `Normal Games`],
            ['Order Type', data.orderType || 'Total Games'],
            ['Games', data.numGames || '10'],
            ['Region', data.region || 'NA'],
        ]);
    } else {
        calculatePrice();
        order_details([
            ['Boost Type', `Normal Games`],
            ['Order Type', 'Total Games'],
            ['Games', '10'],
            ['Region', 'NA'],
        ]);
    }
  }, []);

  useEffect(() => {
    if (initialLoad) return;
    calculatePrice();
    updateOrderDataCookie();
  }, [orderType, numGames, addons]);

  useEffect(() => {
    if (initialLoad) return;
    updateOrderDataCookie();
  }, [selectedChamps, selectedRoles, region]);

  const updateOrderDataCookie = () => {
    order_details([
        ['Boost Type', `Normal Games`],
        ['Order Type', orderType],
        ['Games', numGames],
        ['Region', region],
    ]);
    localStorage.setItem('normal-boosting-data', JSON.stringify({ region, orderType, numGames, addons, selectedChamps, selectedRoles }));
  };

  const calculatePrice = () => {
    loading(true);
    const games = parseInt(numGames, 10);
    if(initialLoad) {
        const stored = localStorage.getItem('normal-boosting-data');
        if (stored) {
            const data = JSON.parse(stored);
            axios.post(
                process.env.REACT_APP_API_URL + '/price-calculation-normal-games-boost',
                { orderType: data.orderType, numGames: data.numGames, addons: data.addons, currency }
            ).then(response => {
                loading(false);
                updatePrice(response.data.totalPrice);
            }).catch(error => {
                loading(false);
                updatePrice(0);
                setSnackbar({ open: true, severity: 'error', message: 'Failed to calculate price.' });
                console.error(error);
            });
            return;
        }
    }
    axios.post(
      process.env.REACT_APP_API_URL + '/price-calculation-normal-games-boost',
      { orderType, numGames: games, addons, currency }
    )
    .then(response => {
      loading(false);
      updatePrice(response.data.totalPrice);
    })
    .catch(error => {
      loading(false);
      updatePrice(0);
      setSnackbar({ open: true, severity: 'error', message: 'Failed to calculate price.' });
      console.error(error);
    });
  };
  
  const handleClose = () => setSnackbar(s => ({ ...s, open:false }));

  
  const toggleAddon = label => {
    setAddons(prev => {
      let next = prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label];
      if (label === 'Duo') next = next.filter(l => l !== 'Solo Only');
      if (label === 'Solo Only') next = next.filter(l => l !== 'Duo');
      return next;
    });
  };

  const toggleRole = key => {
    setSelectedRoles(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  };

    const toggleChamp = name => {
        setSelectedChamps(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
    };

    useEffect(() => {
        if (initialLoad) return;
        if (!addons.includes('Specific Roles')) setSelectedRoles([]);
        if (!addons.includes('Specific Champions')) setSelectedChamps([]);
    }, [addons]);

    const filtered = champions.filter(c => c.toLowerCase().includes(searchChamp.toLowerCase()));

    const pushToDataLayer = (payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    };
    
    useEffect(() => {
        if(checkoutInit) {
            let mergedAddons = [...addons, ...checkoutInit.addons];
            //parse the data for the api
            if (typeof fbq !== 'undefined') {
                fbq('track', 'InitiateCheckout', {
                content_type: 'product',
                content_ids: ['normal-games'],
                content_name: 'Normal Game Boosting',
                currency: currency,
                value: checkoutInit.price,
                num_items: 1,
                contents: [
                    {
                    id: 'normal-games',
                    quantity: 1,
                    item_price: checkoutInit.price
                    }
                ]
                });
            }
            pushToDataLayer({
                event: 'begin_checkout',
                ecommerce: {
                value:    checkoutInit.price,
                currency: currency
                }
            })
            axios.post(process.env.REACT_APP_API_URL + `/user/stripe-checkout`, {
                order_type: 'boost', 
                boost_type: 'normal_games', 
                server: region, 
                coupon: checkoutInit.coupon, 
                normal_games: {
                    order_type: orderType,
                    num_games: parseInt(numGames),
                    addons: mergedAddons,
                    roles: selectedRoles,
                    champions: selectedChamps
                },
                currency: currency
            }, {
                headers: {
                    Authorization: getAccessToken()
                }
            }).then((response) => {
                window.location.href = response.data.url;
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [checkoutInit]);
  

  return (
    <> 
        <Helmet>
            <meta charSet="utf-8" />
            <title>LoL Normal Game Boosting – Level Up Faster | EloFactory.gg</title>
            <meta name="description" content="Boost your account with our LoL normal game service. Skip the grind as pro boosters win normal matches for fast XP. Safe, cheap, and saves you time!" />
            <link rel="canonical" href="https://elofactory.gg/get-boosting/normal-games" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content="LoL Normal Games Boost – Skip the Grind, Level Up Fast" />
            <meta property="og:description" content="Tired of slow leveling? Our normal game boosting lets you bypass the grind. Get experienced boosters to win normals and gain XP quickly and safely." />
            <meta property="og:url" content="https://elofactory.gg/get-boosting/normal-games" />
            <meta property="og:image" content={OG} />
            <meta property="og:image:width"  content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="EloFactory.gg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="LoL Normal Game Boost – Fast Account Leveling" />
            <meta name="twitter:description" content="Skip boring normal games. Our boosting service levels up your LoL account quickly by winning normal matches. Affordable and secure power-leveling!" />
            <meta name="twitter:image" content={OG} />
            <meta name="twitter:site" content="@elofactorygg" />
            <meta name="twitter:creator" content="@elofactorygg" />
            <meta name="robots" content="index, follow" />
            <link rel="alternate" hrefLang="en-US" href="https://elofactory.gg/get-boosting/normal-games" />
            <script type="application/ld+json">{`
            {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "LoL Normal Game Boosting",
            "description": "Normal match boosting service to help level up accounts and win normal games in League of Legends.",
            "provider": {
            "@type": "Organization",
            "name": "EloFactory",
            "url": "https://elofactory.gg"
            },
            "areaServed": "North America",
            "serviceType": "Normal Game Boost / Account Leveling",
            "url": "https://elofactory.gg/get-boosting/normal-games"
            }
            `}
            </script>
        </Helmet>
        {/* Current Rank & LP Gains */}
        <div className="rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] p-6 text-white flex flex-col md:flex-row gap-8">
            <div className="flex-1">
                <h2 className="mb-4 text-lg font-semibold text-[#00baff]">Order Configuration</h2>
                <div className="flex items-start gap-4 items-center">
                    
                    <div className="flex w-full flex-col gap-3">
                        <PremiumSelect
                            label="Order Type"
                            value={orderType}
                            onChange={setOrderType}
                            options={['Total Games', 'Total Wins']}
                        />
                        <PremiumSelect
                            label="Number of Games"
                            value={numGames}
                            onChange={setNumGames}
                            options={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15']}
                        />
                        <PremiumSelect
                            label="Account Region"
                            value={region}
                            onChange={setRegion}
                            options={['NA', 'EUW', 'EUNE', 'LAN']}
                        />
                    </div>
                </div>
            </div>
        </div>

        {/* Add-ons */}
                      <div className={`
                        rounded-lg bg-[var(--bg-surface)] border 
                        border-[var(--border)] p-6 text-white flex 
                        flex-col md:flex-row md:cursor-auto ${addonsOpen ? 'cursor-auto' : 'cursor-pointer'}`}
                        onClick={() => {
                          //check if it is mobile
                          if (window.innerWidth <= 768 && !addonsOpen) {
                            setAddonsOpen(true)
                            return
                          }
                        }}
                      >
                        {/* ─── Left: tight add-ons list ─── */}
                        {/* Header + toggle: only on mobile */}
                        <div className={`flex items-center justify-between md:hidden cursor-pointer md:cursor-auto`}
                        onClick={() => {
                          if (window.innerWidth <= 768 && addonsOpen) {
                            setAddonsOpen(!addonsOpen)
                            return
                          }
                        }}>
                          <h2 className="text-lg font-semibold text-[#00BAFF]">Advanced Settings</h2>
                          <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`
                                text-white
                                transform transition-transform duration-300 ease-in-out
                                ${addonsOpen ? 'rotate-180' : 'rotate-0'}
                              `}
                              width={16}
                              height={16}
                            />        
                          </div>
                        <div
                        className={`
                          overflow-hidden transition-[max-height] duration-300 ease-in-out
                          ${addonsOpen ? 'max-h-[2000px]' : 'max-h-0'}
                          md:max-h-none md:block space-y-4
                        `}
                      >
                        <h2 className="text-lg font-semibold text-[#00baff] hidden md:block">Advanced Settings</h2>
            {[
                ['Duo', '+60%', 'Play alongside the booster in Duo Queue.', faUserGroup],
                ['Priority', '+15%', 'Your order will be prioritized before others.', faUserClock],
                ['Solo Only', '+25%', 'The booster will not duo queue with anyone.', faUser],
                ['Streaming', '+15%', 'Watch your boost live via private stream.', faWifi],
            ].map(([label, extra, desc, icon]) => (
                <div key={label} className="flex items-center justify-between">
                <div className="flex items-center mr-4">
                    <FontAwesomeIcon icon={icon} className="w-4 h-4 mr-2 text-[#00baff]" />
                    <span className="text-sm text-white mr-2">{label}</span>
                    <span className="text-sm text-[#00baff] mr-2">{extra}</span>
                    <LPTooltip tooltipText={desc} />
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch ml-2"
                    checked={addons.includes(label)}
                    onChange={() => toggleAddon(label)}
                />
                </div>
            ))}

            <div className="space-y-4">
                {/* Header with toggle */}
                <div className="flex items-center justify-between" key="Specific Roles">
                <div className="flex items-center">
                    <FontAwesomeIcon
                    icon={faLocationCrosshairs}
                    className="w-4 h-4 mr-2 text-[#00baff]"
                    />
                    <span className="text-sm text-white mr-2">Specific Roles</span>
                    <span className="text-sm text-[#00baff] mr-2">+15%</span>
                    <LPTooltip tooltipText="Choose a preferred role (e.g. Mid, ADC)." />
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch"
                    checked={addons.includes("Specific Roles")}
                    onChange={() => toggleAddon("Specific Roles")}
                />
                </div>

                {/* Role buttons */}
                <div className={`
                flex flex-wrap gap-2 justify-between
                ${addons.includes("Specific Roles") ? "flex" : "hidden"} md:flex
                `}>
                {roles.map(({ key, icon }) => (
                    <button
                    key={key}
                    onClick={() => toggleRole(key)}
                    disabled={!addons.includes("Specific Roles")}
                    aria-pressed={selectedRoles.includes(key)}
                    className={
                        `
                        group relative flex items-center justify-center
                        rounded-md
                        transition-transform transition-shadow transition-opacity duration-200 ease-out
                        motion-reduce:transition-none
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${
                        selectedRoles.includes(key)
                            ? "shadow-[0_0_6px_rgba(0,186,255,0.6)]  bg-white/10"
                            : "shadow-sm bg-transparent"
                        }
                        hover:shadow-[0_0_4px_rgba(0,186,255,0.4)] hover:bg-white/5
                        `
                    }
                    >
                    <img
                        src={icon}
                        alt={`${key} Role Icon`}
                        title={`League of Legends ${key} role`}
                        className="w-8 h-8"
                        width={32}
                        height={32}
                    />
                    </button>
                ))}
                </div>
            </div>
            
            </div>

            {/* ─── Divider ─── */}
            <div className="mx-6 border-l border-[var(--border)] hidden md:block" />

            {/* ─── Right: full-width panels ─── */}
            <div className={`flex-1 space-y-4 pl-0 md:pl-2
            overflow-hidden transition-all duration-300 ease-in-out
            ${addonsOpen ? 'max-h-[2000px] mt-4' : 'max-h-0 mt-0 space-y-0'}
            md:max-h-none md:block`}
            >
            {/* Specific Champions panel */}
            <div className="space-y-4">
                {/* header + toggle */}
                <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faUsers} className="w-4 h-4 mr-2 text-[#00baff]" />
                    <span className="text-sm text-white mr-2">Specific Champions</span>
                    <span className="text-sm text-[#00baff] mr-2">+20%</span>
                    <LPTooltip tooltipText="Choose specific champions to play." />
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch"
                    checked={addons.includes("Specific Champions")}
                    onChange={() => toggleAddon("Specific Champions")}
                />
                </div>

                {/* ─── Mobile-hide / desktop-show wrapper ─── */}
            <div className={`${addons.includes("Specific Champions") ? 'block' : 'hidden'} md:block space-y-2`}>
                
                {/* ─── Search box (unstyled, just your original class) ─── */}
                <input
                type="text"
                placeholder="Search champions…"
                value={searchChamp}
                onChange={e => setSearchChamp(e.target.value)}
                disabled={!addons.includes("Specific Champions")}
                className="search-input w-full rounded-md bg-[var(--bg-surface)] border border-[var(--border)] px-3 py-2 text-sm text-white placeholder-[var(--secondary-text)] focus:outline-none focus:ring-1 focus:ring-[var(--focus-ring)]"
                />

                {/* ─── Champions container: grid when enabled, .is-disabled when off ─── */}
                <div
                className={`
                    champions-container
                    scrollbar-custom
                    h-44
                    ${addons.includes("Specific Champions")
                    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 overflow-y-auto rounded-md bg-[var(--bg-surface)] border border-[var(--border)]'
                    : 'is-disabled'}
                `}
                >
                {filtered.map(champ => (
                    <label key={champ} className="champion-item flex items-center p-1 rounded">
                    <input
                        type="checkbox"
                        checked={selectedChamps.includes(champ)}
                        onChange={() => {
                            //champion selection maximum of 10
                            if (selectedChamps.length >= 10 && !selectedChamps.includes(champ)) {
                                setSnackbar({ open: true, severity: 'warning', message: 'You can only select up to 10 champions.' });
                                return;
                            }
                            toggleChamp(champ)
                        }}
                        disabled={!addons.includes("Specific Champions")}
                    />
                    <span className="champion-name">{champ}</span>
                    </label>
                ))}
                </div>
            </div>

            </div>
            </div>
        </div>

        <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
        >
            <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width:'100%' }}>
                {snackbar.message}
            </Alert>
        </Snackbar>
    </>
    );
}